import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { Data } from '.generated/templates/Feature.Data.model';
import Button from 'components/helpers/Button';
import { classnames } from 'tailwindcss-classnames';
import { getEnumValue } from 'lib/util/enum';
import { ValidHeadingLevels, ValidHeadingSizes, VideoSides } from 'lib/sitecore/component-params';
import { env } from 'lib/env';
import Heading from '../Heading';

export type VideoItemProps = Sitecore.Override.ItemEx &
  Data.Video.Fields.VideoItem & {
    videoTitleHeadlineLevel?: Sitecore.Override.ItemEx;
    videoTitleHeadlineSizing?: Sitecore.Override.ItemEx;
    videoSide?: Sitecore.Override.ItemEx;
    hideVideoContent?: boolean;
  };

const VideoItem = ({
  fields,
  videoTitleHeadlineLevel,
  videoTitleHeadlineSizing,
  videoSide,
  hideVideoContent,
}: VideoItemProps): JSX.Element => {
  if (!fields) {
    return <></>;
  }

  const videoHeading = videoTitleHeadlineLevel
    ? (getEnumValue(videoTitleHeadlineLevel) as ValidHeadingLevels)
    : 'h2';
  const videoHeadingSize = videoTitleHeadlineSizing
    ? (getEnumValue(videoTitleHeadlineSizing) as ValidHeadingSizes)
    : 'l';
  const videoSideValue = videoSide ? (getEnumValue(videoSide) as VideoSides) : 'right';
  const videoContainerClasses = classnames(
    'video-container',
    'flex',
    'h-full',
    'flex-col-reverse',
    'justify-between',
    'lg:grid',
    'lg:grid-cols-2',
    'lg:gap-7'
  );
  const videoContentClasses = classnames(
    'video-content',
    'z-10',
    'flex',
    'flex-grow',
    'flex-col',
    'justify-center',
    'lg:self-center',
    'lg:w-auto',
    {
      'lg:order-2': videoSideValue === 'left',
      'lg:order-1': videoSideValue !== 'left',
    }
  );
  const videoItemClasses = classnames('video-item', 'z-10', 'lg:w-auto', 'mb-6', 'lg:mb-0', {
    'lg:order-1': videoSideValue === 'left',
    'lg:order-2': videoSideValue !== 'left',
  });

  const videoEmbedUrl = (() => {
    if (!fields?.videoEmbedUrl?.value) {
      return '';
    }
    const targetUri = new URL(fields.videoEmbedUrl?.value ?? '');
    if (!targetUri) {
      return '';
    }
    const sourceUri = new URL(env.PUBLIC_URL);
    const appendQuery =
      (targetUri.search?.length ?? 0) > 1 ? `&${targetUri.search.substring(1)}` : '';
    return `${targetUri.origin}${targetUri.pathname}?enablejsapi=1&origin=${
      sourceUri?.origin ?? ''
    }${appendQuery}`;
  })();

  return (
    <div data-component="authorable/general/videoitem" className={classnames('h-full')}>
      {/* We need left option in Sitecore for this item, by default the video will align right */}
      {/* <div className={classnames('video-container', 'video-align-left')}> */}
      <div className={videoContainerClasses}>
        {hideVideoContent ? (
          <></>
        ) : (
          <div className={videoContentClasses}>
            <Heading
              className={classnames('mb-4')}
              description={fields.videoDescription}
              headingText={fields.videoTitle}
              headingLevel={videoHeading}
              headingSize={videoHeadingSize}
            />
            <div className={classnames('flex', 'items-baseline', 'flex-wrap', 'justify-start')}>
              <Button
                className={classnames(
                  'mt-6',
                  'sm:mr-6',
                  'w-full',
                  'sm:w-auto',
                  'text-center',
                  'md:text-left'
                )}
                linkField={fields.cta}
              />
              <Button
                linkField={fields.cta2}
                variant="secondary"
                className={classnames('mt-4', 'w-full', 'sm:w-auto', 'text-center', 'md:text-left')}
              />
            </div>
          </div>
        )}

        <div className={videoItemClasses}>
          <div className={classnames('video-wrapper', 'overflow-hidden', 'rounded-xl')}>
            <iframe
              className={classnames('video-iframe', 'h-full', 'w-full')}
              title={fields.videoTitle?.value}
              src={videoEmbedUrl}
              referrerPolicy="strict-origin-when-cross-origin"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoItem;
